import dayjs from "dayjs";
import * as yup from "yup";
import { isSomething } from "../common-utils";

const validationMessage = {
  nameReg: () => `Only letters, number and underscores are allowed`,
  required: (name: string) => `${name} is a required field.`,
  lettersReg: () => `Only letters are allowed`,
  maxStrSize: (name: string) => `${name} must be at most 31 characters`,
  minStrSize: (name: string) => `${name} must be at least 3 characters`,
  mustMatch: () => "Password and Confirm password must match",
  email: (name: string) => `${name} must be a valid email`,
};

export const signUpFormValidation = () => {
  return yup.object().shape({
    name: yup
      .string()
      .required(validationMessage.required("Name"))
      .max(31, validationMessage.maxStrSize("Name"))
      .min(3, validationMessage.minStrSize("Name")),
    email: yup
      .string()
      .email(validationMessage.email("Email"))
      .required("Please enter a valid email."),
    password: yup.string().required(validationMessage.required("Password")),
    confirm: yup
      .string()
      .oneOf([yup.ref("password")], validationMessage.mustMatch())
      .required(),
  });
};

export const loginFormValidation = () => {
  return yup.object().shape({
    email: yup
      .string()
      .email(validationMessage.email("Email"))
      .required("Please enter a valid email."),
    password: yup.string().required(validationMessage.required("Password")),
  });
};

export const UpdatePasswordFormValidation = () => {
  return yup.object().shape({
    password: yup.string().required("Please enter your password."),
    confirm: yup
      .string()
      .oneOf([yup.ref("password")], validationMessage.mustMatch())
      .required(),
  });
};

export const ResetPasswordFormValidation = () => {
  return yup.object().shape({
    email: yup
      .string()
      .email(validationMessage.email("Email"))
      .required("Please enter a valid email."),
  });
};

export const clientFormValidation = () => {
  return yup.object().shape({
    firstName: yup
      .string()
      .required(validationMessage.required("First Name"))
      .max(31, validationMessage.maxStrSize("First Name"))
      .min(3, validationMessage.minStrSize("First Name")),
    lastName: yup
      .string()
      .required(validationMessage.required("Last Name"))
      .max(31, validationMessage.maxStrSize("Last Name"))
      .min(3, validationMessage.minStrSize("Last Name")),
    email: yup
      .string()
      .email(validationMessage.email("Email"))
      .required("Please enter a valid email."),
    address: yup.object().required(),
    numberOfPets: yup.number().required(),
    mobile: yup
      .string()
      .min(4, validationMessage.minStrSize("Phone Number"))
      .required("Phone Number is a required Field"),
  });
};

export const petFormValidation = () => {
  return yup.object().shape({
    name: yup
      .string()
      .required(validationMessage.required("Name"))
      .max(31, validationMessage.maxStrSize("Name"))
      .min(3, validationMessage.minStrSize("Name")),
    breed: yup.string().required(),
    species: yup.string().required(),
    birthdate: yup.date().required("Date of Birth is required").nullable(),
    gender: yup.string().required(),
    color: yup.string(),
  });
};
export const timeSlotValidation = () => {
  return yup.object().shape({
    date: yup.date().required("Date is required").nullable(),
    fromTime: yup.string().required("From time is required"),
    toTime: yup
      .string()
      .required("To time is required")
      .test(
        "is-greater",
        "End time must be greater than start time",
        function (value) {
          const { fromTime } = this.parent;
          return dayjs(value, "HH:mm").isAfter(dayjs(fromTime, "HH:mm"));
        }
      ),
  });
};

export const clientValidation = () => {
  return yup.object().shape({
    search: yup.string().required(),
    address: yup.string().required(),
  });
};

export const inviteUserFormValidation = () => {
  return yup.object().shape({
    email: yup
      .string()
      .email(validationMessage.email("Email"))
      .required("Please enter a valid email."),
    name: yup.string().required(validationMessage.required("Name")),
  });
};

export const UserFormValidation = (activeStep: number) => {
  return yup.object().shape({
    email:
      activeStep === 2
        ? yup
          .string()
          .email(validationMessage.email("Email"))
          .required("Please enter a valid email.")
        : yup.string().notRequired(),
  });
};

export const addBlockOffsValidationSchema = yup.object().shape({
  add_for: yup.string().required("Add For is required"),
  title: yup.string().required("Title is required"),
  start_date: yup.date().required("Start Date is required"),
  end_date: yup.date().when("repeat_interval", (repeat_interval, schema) => {
    if (isSomething(repeat_interval[0])) {
      return schema
        .required("End Date is required")
        .min(
          yup.ref("start_date"),
          "End Date must be the same or later than Start Date"
        );
    }
    return schema.nullable();
  }),
  start_time: yup.string().required("Start Time is required"),
  end_time: yup.string().required("End Time is required"),
});

export const timeAdjustmentSchema = (appointmentTypes: any) => {
  let shape: Record<string, any> = {};
  appointmentTypes.forEach((type: any) => {
    shape[type.integration_id] = yup
      .number()
      .typeError(`${type.label} must be a number`)
      .min(1, `${type.label} must be at least 1`)
      .max(30, `${type.label} cannot exceed 30`)
      .required(`${type.label} is required`);
  });
  // shape["wellness_visits_per_day"] = yup
  //   .number()
  //   .typeError("Wellness Visits per day must be a number")
  //   .min(1, "Must be at least 1")
  //   .max(50, "Cannot exceed 50")
  //   .required("Wellness Visits per day is required");

  return yup.object().shape(shape);
};

export const travelClientSchema = yup.object().shape({
  client_id: yup
    .string()
    .required("Client is required")
    .nullable(),
  travel_client_id: yup
    .string()
    .required("Travel Client is required")
    .nullable(),
  client_search: yup.string(),
  travel_client_search: yup.string(),
});

export const settingsSchema = yup.object().shape({
  workday_start_utc: yup.string().required("Workday start time is required"),
  workday_end_utc: yup.string().required("Workday end time is required"),
  time_slot_limit: yup.string().required("Time slot limit is required"),
  block_gen_limit: yup.string().required("Block generation limit is required"),
  block_gen_min: yup.string().required("Block generation minimum is required"),
  block_gen_offset: yup
    .string()
    .required("Block generation offset is required"),
  enable_matrix_filtering: yup
    .string()
    .required("Matrix filtering is required"),
  enable_additional_rules: yup
    .string()
    .required("Additional rules is required"),
  enable_reserve_filtering: yup
    .string()
    .required("Reserve filtering is required"),
  lookahead: yup.string().required("Look ahead is required"),
  // address: yup.string().required("Address is required"),
  // lat: yup.string().required("Latitude is required"),
  // lon: yup.string().required("Longitude is required"),
});

export const InitialPetDetailsSchema = yup.object().shape({
  appointment_for: yup.string().required("Schedule For is required"),
  search: yup.string().required("Client search is required"),
  address: yup.object().required("Address is required"),
});

export const ReservedDataValidation = () => {
  const schema = yup
    .object()
    .shape({})
    .test("validate-priority-and-reserve", function (values: any) {
      const dayPriorityMap: Record<string, Set<number>> = {};

      for (const key in values) {
        const [day, field] = key.split("_");
        const value = Number(values[key]);

        if (field === "priority") {
          if (value < 1 || value > 10) {
            return this.createError({
              path: key,
              message: `Priority must be between 1 and 10`,
            });
          }

          if (!dayPriorityMap[day]) {
            dayPriorityMap[day] = new Set();
          }

          if (dayPriorityMap[day].has(value)) {
            return this.createError({
              path: key,
              message: `Duplicate priority ${value} found for day ${day}`,
            });
          }

          dayPriorityMap[day].add(value);
        }

        if (field === "reserved") {
          if (value < 15 || value > 480) {
            return this.createError({
              path: key,
              message: `Reserved time must be between 15 and 480 minutes.`,
            });
          }

          if (value % 15 !== 0) {
            return this.createError({
              path: key,
              message: `Reserved time must be in 15-minute increments.`,
            });
          }
        }
      }

      return true;
    });

  return schema;
};
